import { useCallback, useId, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectOption } from '@/components/ui/select';
import { api } from '@/lib/api';
import { GRACE_OPTIONS, hostInDomain, switchErrorMessage } from '@/lib/domainSwitch';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { App } from '@/types/app';
import type { DomainSummary, DomainSwitch } from '@/types/domain';

interface ChangeDomainDialogProps {
  /** null keeps the dialog closed. */
  app: App | null;
  domains: DomainSummary[];
  onClose: () => void;
  /** The switch the server queued (202). */
  onStarted: (sw: DomainSwitch) => void;
}

/** Shell always mounted; the form mounts only while an app is set, so state resets on every open. */
export function ChangeDomainDialog({ app, domains, onClose, onStarted }: ChangeDomainDialogProps) {
  const submittingRef = useRef(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={app !== null}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {app ? (
        <FormBody
          key={app.id}
          app={app}
          domains={domains}
          onClose={onClose}
          onStarted={onStarted}
          submittingRef={submittingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface FormBodyProps extends Omit<ChangeDomainDialogProps, 'app'> {
  app: App;
  submittingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

function FormBody({ app, domains, onClose, onStarted, submittingRef, captureOpener, restoreFocus }: FormBodyProps) {
  const ids = useId();
  const selectRef = useRef<HTMLSelectElement>(null);
  const targets = domains.filter((d) => d.name !== app.domain);
  const [domain, setDomain] = useState(() => (targets.length === 1 ? targets[0].name : ''));
  const [grace, setGrace] = useState(String(GRACE_OPTIONS[1].minutes));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  const host = app.full_subdomain ?? '';
  const newHost = domain && host ? hostInDomain(host, domain) : null;
  const target = targets.find((d) => d.name === domain);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!domain) {
      setMissing(true);
      selectRef.current?.focus();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const sw = await api.apps.switchDomain(app.id, { domain, grace_period_minutes: Number(grace) });
      toast.success(`Moving ${app.display_name || app.name} to ${domain}`);
      onStarted(sw);
    } catch (err) {
      setError(switchErrorMessage(err));
      setSubmitting(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const blockWhileSubmitting = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        selectRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={blockWhileSubmitting}
      onPointerDownOutside={blockWhileSubmitting}
      onInteractOutside={blockWhileSubmitting}
    >
      <DialogHeader>
        <DialogTitle>Change domain</DialogTitle>
        <DialogDescription>
          Move '{app.display_name || app.name}' from <span className="font-mono">{app.domain}</span> to another domain. Both
          hostnames work until the new one is verified and the grace period ends.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        <div className="space-y-2">
          <label htmlFor={`${ids}-domain`} className="text-sm font-medium">
            New domain
          </label>
          <Select
            ref={selectRef}
            id={`${ids}-domain`}
            value={domain}
            onValueChange={(v) => {
              setDomain(v);
              setMissing(false);
            }}
            aria-invalid={missing || undefined}
            aria-describedby={missing ? `${ids}-domain-error` : `${ids}-host`}
          >
            <SelectOption value="">Select a domain…</SelectOption>
            {targets.map((d) => (
              <SelectOption key={d.id} value={d.name}>
                {d.name}
              </SelectOption>
            ))}
          </Select>
          {missing ? (
            <p id={`${ids}-domain-error`} className="text-xs text-destructive">
              Choose the domain to move to
            </p>
          ) : null}
          <p id={`${ids}-host`} className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {newHost ? (
              <>
                New hostname: <span className="font-mono text-foreground">{newHost}</span>
                {target?.new_app_tls_mode === 'origin' ? ' (Cloudflare origin certificate)' : " (Let's Encrypt)"}
              </>
            ) : (
              <>
                Current hostname: <span className="font-mono text-foreground">{host || '—'}</span>
              </>
            )}
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor={`${ids}-grace`} className="text-sm font-medium">
            Old hostname
          </label>
          <Select id={`${ids}-grace`} value={grace} onValueChange={setGrace} aria-describedby={`${ids}-grace-hint`}>
            {GRACE_OPTIONS.map((o) => (
              <SelectOption key={o.minutes} value={String(o.minutes)}>
                {o.label}
              </SelectOption>
            ))}
          </Select>
          <p id={`${ids}-grace-hint`} className="text-xs text-muted-foreground">
            How long <span className="font-mono">{host || 'the old hostname'}</span> keeps working after the switch, so
            clients and links can move over.
          </p>
        </div>

        <p className="rounded-md bg-status-warning-soft p-3 text-sm text-status-warning">
          The app's containers restart once to add the new hostname. Other changes to the app are blocked until the new
          hostname is verified.
        </p>

        {error ? (
          <p ref={alertRef} tabIndex={-1} role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive outline-none [overflow-wrap:anywhere]">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" aria-disabled={submitting} onClick={() => !submitting && onClose()}>
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting} aria-busy={submitting}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Starting…' : 'Change domain'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
